import * as React from "react";
import { useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import LobsterIcon from "assets/img/svg/lobster.svg?react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Logo } from "components/ui/logo";
import { setPostLoginRedirect } from "utils/redirects";
import { cn } from "lib/utils";
import { isAgentsRoute, isNemoClawRoute } from "constants/agentLaunchables";

export interface LandingHeaderNavItem {
  label: string;
  href: string;
  active?: boolean;
  chip?: string;
  hidden?: boolean;
  lobster?: boolean;
  onClick?: () => void;
}

interface LandingHeaderProps {
  redirectUrl?: string;
  navItems?: LandingHeaderNavItem[];
  navSlot?: React.ReactNode;
  afterNavSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  position?: "sticky" | "static";
  size?: "default" | "compact";
  onLogoClick?: () => void;
}

function HeaderNavLink({ item }: { item: LandingHeaderNavItem }) {
  const isExternal = /^https?:\/\//.test(item.href);
  const className = cn(
    "group flex h-full items-center gap-1.5 border-b-2 border-transparent pt-1 text-sm text-muted-foreground transition-colors hover:text-foreground",
    item.active && "border-highlight text-foreground",
  );
  const content = (
    <>
      {item.label}
      {item.lobster && (
        <LobsterIcon
          className={cn(
            "h-3.5 w-3.5 flex-shrink-0",
            item.active
              ? ""
              : "[&_path]:fill-muted-foreground group-hover:[&_path]:fill-foreground",
          )}
        />
      )}
      {item.chip && (
        <Badge
          variant="outline"
          className="ml-1 h-5 px-1.5 text-[10px] uppercase"
        >
          {item.chip}
        </Badge>
      )}
    </>
  );

  const handleClick = (event: React.MouseEvent) => {
    if (item.onClick) {
      event.preventDefault();
      item.onClick();
    }
  };

  if (isExternal) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={item.href} className={className} onClick={handleClick}>
      {content}
    </Link>
  );
}

function CustomNav({
  navItems,
  afterNavSlot,
}: {
  navItems: LandingHeaderNavItem[];
  afterNavSlot?: React.ReactNode;
}) {
  return (
    <div className="ml-6 hidden h-8 items-center gap-6 lg:flex">
      {navItems
        .filter((item) => !item.hidden)
        .map((item) => (
          <HeaderNavLink key={`${item.label}-${item.href}`} item={item} />
        ))}
      {afterNavSlot}
    </div>
  );
}

export function LandingHeader({
  redirectUrl,
  navItems,
  navSlot,
  afterNavSlot,
  rightSlot,
  position = "sticky",
  size = "compact",
  onLogoClick,
}: LandingHeaderProps = {}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignIn = () => {
    if (redirectUrl) {
      setPostLoginRedirect(redirectUrl);
    }
    history.push("/login");
  };

  const defaultPublicNavItems: LandingHeaderNavItem[] = [
    {
      label: "GPUs",
      href: "/environment/new/public",
    },
    {
      label: "Agents",
      href: "/agents",
    },
    {
      label: "Launchables",
      href: "/launchables",
    },
  ];

  const isNavItemActive = (item: LandingHeaderNavItem) => {
    if (item.active !== undefined) return item.active;

    const pathname = location.pathname;
    const agentsActive = isAgentsRoute(pathname, location.search);
    const nemoClawActive = isNemoClawRoute(pathname, location.search);

    if (item.href === "/environment/new/public") {
      return pathname === "/environment/new/public";
    }
    if (item.href === "/agents") {
      return agentsActive;
    }
    if (item.href === "/nemoclaw") {
      return nemoClawActive;
    }
    if (item.href === "/launchables") {
      return (
        (!agentsActive &&
          !nemoClawActive &&
          pathname.startsWith("/launchables")) ||
        (!agentsActive &&
          !nemoClawActive &&
          pathname.startsWith("/launchable/deploy"))
      );
    }
    return pathname === item.href;
  };

  const resolvedNavItems = (navItems ?? defaultPublicNavItems).map((item) => ({
    ...item,
    active: isNavItemActive(item),
  }));

  const logo = (
    <Logo
      src="/logos/svg/nvidia-white-text.svg"
      alt="NVIDIA logo"
      width="84px"
      height="30px"
    />
  );

  return (
    <header
      className={cn(
        "bg-background/95 supports-[backdrop-filter]:bg-background/60 z-50 w-full backdrop-blur transition-[border-color] duration-200",
        size === "compact" ? "h-12" : "h-16",
        position === "sticky" && "sticky top-0",
        isScrolled && "border-b border-border",
      )}
    >
      <nav
        className={cn(
          "relative mx-auto flex w-full items-center justify-between gap-4 px-4 duration-200 lg:px-6 xl:max-w-7xl",
          size === "compact" ? "py-2" : "py-4",
        )}
      >
        <div className="flex items-center">
          {onLogoClick ? (
            <button
              type="button"
              className="flex items-center gap-2 font-semibold"
              onClick={onLogoClick}
            >
              {logo}
            </button>
          ) : (
            <Link to="/" className="flex items-center gap-2 font-semibold">
              {logo}
            </Link>
          )}

          {navSlot ? (
            <div className="ml-6 hidden h-8 items-center lg:flex">
              {navSlot}
            </div>
          ) : (
            <CustomNav
              navItems={resolvedNavItems}
              afterNavSlot={afterNavSlot}
            />
          )}
        </div>

        {rightSlot ?? (
          <div className="flex gap-2">
            <Button variant="outline" className="h-8" asChild>
              <Link to="/help?product=brev">Contact Us</Link>
            </Button>
            <Button className="h-8" onClick={handleSignIn}>
              Sign In
            </Button>
          </div>
        )}
      </nav>
    </header>
  );
}
